import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  admobRow,
  admobOverseasRow,
  CHILDREN,
  CRASHLYTICS_ROW,
  CRASHLYTICS_OVERSEAS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  REMEDIES,
  RETENTION_HEADERS,
  SECURITY,
  SUPPORT_ITEM_INPUT,
  SUPPORT_ITEM_AUTO,
  SUPPORT_RETENTION_ROW,
  SUPPORT_NOTE,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  rights,
  table,
} from './common'

/**
 * Kite 개인정보 처리방침 — 공개 열람·선택적 로그인과 계정 즐겨찾기.
 *
 * 공고 열람은 공개이며 소셜 로그인·계정 즐겨찾기는 선택 사항이다.
 * 네이티브 앱의 Crashlytics와 공용 계정·문의 화면의 처리 항목도 함께 고지한다.
 *
 * 접속 기록 14일은 메인서버 Loki 의 보존 기간(`infra/loki/loki.yml` retention_period 336h)이다 —
 * 그 값을 바꾸면 이 문장도 같이 고친다. 컨테이너 자체 로그는 용량 단위로 먼저 덮어쓴다.
 *
 * 웹은 문의 링크를 열고 앱은 공용 웹뷰를 연다.
 */

const EFFECTIVE_DATE = 'September 24, 2026'
const LAST_REVISED = 'October 6, 2026'

const KITE_SUPPORT_PURPOSE =
  'Handling support enquiries sent from cottonray.com/support, opened by a website link or a screen inside the app, and replying by email where you have given an address'

const ADS_PURPOSE = 'Serving non-personalised banner ads in the native app, measuring ad performance and preventing invalid traffic'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Operating the website securely — keeping the server access records needed to deliver pages, stop abuse, and investigate faults',
      'Providing optional accounts and synchronising saved job postings across your devices',
      'Diagnosing app crashes and improving stability with Firebase Crashlytics',
      KITE_SUPPORT_PURPOSE,
      ADS_PURPOSE,
    ],
  },
  {
    heading: 'Article 2. Public browsing and optional account synchronisation',
    paragraphs: [
      'Kite gathers public job postings from company recruitment sites. You can browse without an account. Optional sign-in uses Kakao, Google or Apple (where available). The chosen provider supplies an account identifier, email address and display name where permitted. Kite does not receive your social account password. Authentication and account linking are handled by the Operator’s account service and Keycloak.',
      'Saving a favourite requires sign-in. Saved posting IDs and save times are stored on the Operator’s server under your account ID to synchronise across devices. Clearing browser data or removing the app does not delete account favourites.',
      'Selecting a posting opens the original recruitment site in a new window. What that site collects is governed by that company’s own privacy policy; the Operator passes it nothing about you. Company logos shown next to postings are fetched and stored by the Operator’s server, so viewing them does not send your request to any third party.',
    ],
  },
  {
    heading: 'Article 3. Personal data processed',
    paragraphs: ['The items below are everything the Operator processes.'],
    bullets: [
      '[Collected automatically] Server access records — IP address, time of the request, the address requested, and the browser’s user-agent string, recorded by the web server whenever a page or data file is requested',
      '[Optional account] Account ID, linked social provider identifiers, display name, email address, authentication tokens and session records; profile photographs only when you choose to upload one in My account',
      '[Signed-in favourites] Account ID, saved posting IDs and save times',
      '[App diagnostics] Installation identifiers, the account ID while signed in, app version and build, device model, operating system, crash traces and diagnostic logs; favourite posting IDs are not attached to Crashlytics reports',
      '[Native-app advertising] Device information, advertising identifiers where available, coarse IP-based location, ad interactions and diagnostics handled by Google AdMob; no precise device location permission is requested',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
    ],
    note: 'Public browsing does not require registration. The native app requests non-personalised banner ads. Google may process device and advertising data to deliver and measure ads and prevent fraud. The website contains no AdMob SDK.',
  },
  {
    heading: 'Article 4. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      ['Account and synchronised favourites', 'Until account deletion; individual favourites can be removed at any time', 'Providing the optional account service you request'],
      ['App diagnostics', '90 days from collection before Firebase starts removing the data', 'Service stability and error diagnosis'],
      ['Server access records', '14 days from the request, after which the log store deletes them automatically', 'Legitimate interest in operating the service securely'],
      ['Native-app advertising', 'Subject to Google’s retention policy', 'Ad delivery, measurement and prevention of invalid traffic'],
      [...SUPPORT_RETENTION_ROW],
    ]),
    note: `${SUPPORT_NOTE} The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute.`,
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      ['Kakao Corp.', 'Optional social sign-in', 'Provider account identifier, profile name and email where permitted', 'During authentication; linked identifiers remain until account deletion'],
      ['Google LLC / Apple Inc.', 'Optional social sign-in', 'Provider account identifier, name and email where permitted', 'During authentication; linked identifiers remain until account deletion'],
      admobRow(ADS_PURPOSE),
      CRASHLYTICS_ROW.map((value, index) => index === 2 ? `${value}; account ID while signed in` : value),
      cloudflareRow('Profile photographs you upload and photographs attached to support messages', 'Profile photographs until replacement or account deletion; support photographs for 1 year'),
    ]),
    note: 'The website and its server access records are run on the Operator’s own server; that work is entrusted to no one. In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If the processors change, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'Using the native app sends diagnostics and, when ads can be requested under the applicable consent choices, advertising data to Google. Choosing Google or Apple sign-in and uploading photographs also involve the overseas processors below. Kakao is based in Korea.',
    ],
    table: table(OVERSEAS_HEADERS, [
      admobOverseasRow(ADS_PURPOSE),
      CRASHLYTICS_OVERSEAS_ROW.map((value, index) => index === 2 ? `${value}; account ID while signed in` : value),
      ['Google LLC', 'The United States and other countries where Google operates', 'Provider account identifier, name, email and authentication request', 'Over HTTPS when you choose Google sign-in', 'Authenticating the selected account', 'During authentication; the provider applies its own account retention policy'],
      ['Apple Inc.', 'The United States', 'Provider account identifier, name, email and authentication request', 'Over HTTPS when you choose Sign in with Apple', 'Authenticating the selected account', 'During authentication; the provider applies its own account retention policy'],
      cloudflareOverseasRow('Profile photographs you upload and photographs attached to support messages', 'Profile photographs until replacement or account deletion; support photographs for 1 year'),
    ]),
    note: `${OVERSEAS_REFUSAL} To avoid native-app diagnostics and advertising data collection, use the public Kite website instead. Social sign-in and photograph uploads are optional.`,
  },
  DISPOSAL,
  rights(
    [
      'For account favourites — remove the star while signed in. Use My account → Delete account to delete your account, profile photograph and synchronised favourites. The same account page is available at cottonray.com/account; you may also contact support from your account email address',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'your account information, synchronised favourites and support messages',
  ),
  {
    heading: 'Installation and operation of automatic collection devices, and how to refuse them',
    paragraphs: [
    'Login uses authentication cookies and session tokens; native-app session tokens are kept in the operating system’s secure storage. Account favourites are sent to the Operator for synchronisation. Firebase Crashlytics automatically sends native-app diagnostics and installation identifiers. Google AdMob serves non-personalised banners separately and may process the advertising identifier, device information, coarse IP-based location and ad interactions under Google’s retention policy. Where required, Google’s consent form appears before an ad request. Use My account → Ad settings when that option is available to review or change your privacy choices. You can refuse or change applicable consent choices in that form and control advertising identifiers in your device settings.',
    ],
  },
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(LAST_REVISED, ['September 24, 2026 — established and brought into effect', 'October 6, 2026 — updated for native apps, optional social sign-in, account deletion, Crashlytics diagnostics and non-personalised AdMob banner ads']),
]

export const KITE_POLICY: PolicyDocument = {
  slug: 'kite',
  title: 'Kite Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for Kite (“the service”, kite.cottonray.com) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
